import { useCallback, type PropsWithChildren } from 'react';
import styled, { useTheme, type DefaultTheme } from 'styled-components';

import { SettingsPaths } from '@src/components/NavigationSidebar/hooks/getNavPath';
import {
  EllipsisBoxWithTooltip,
  HStack,
  Icon,
  IconName,
  Menu,
  MenuItem,
  parseDimension,
  ToastSimpleWrapper,
  VStack,
  type BoxProps,
} from '@talos/kyoko';
import { useNavigate } from 'react-router-dom';
import { AppInboxSeverityIcon } from './AppInboxSeverityIcon';
import { ToastDate } from './ToastDate';
import { AppNotificationSeverity, type AppInboxToastStateItem } from './types';

export interface AppInboxToastBaseProps extends AppInboxToastStateItem {
  /** Where the notification is to be displayed */
  containerVariant: 'inbox' | 'toast';
  wrapperWidth?: BoxProps['w']; // Width for the toast content in this context
  onRemove?: (id: string) => void;
  onToastClick?: (id: string) => void;
  'data-testid'?: string;
  /** drop onclick to avoid confusion */
  boxProps?: Omit<BoxProps, 'onClick'>;
  lineClamp?: number;
}

/** Base display implementation for App Notification items */
export const AppInboxToastBase = ({
  'data-testid': dataTestId,
  id: toastId,
  wrapperWidth,
  containerVariant,
  variant,
  title,
  description,
  timeStamp,
  lineClamp = 2,
  boxProps,
  onRemove,
  onToastClick, // onClick handler for the toast, typically to progress the notification state
}: PropsWithChildren<AppInboxToastBaseProps>) => {
  const theme = useTheme();

  const handleClick: React.MouseEventHandler<HTMLElement> = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      onToastClick?.(toastId);
    },
    [onToastClick, toastId]
  );
  const handleRemove: React.MouseEventHandler<HTMLElement> = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      onRemove?.(toastId);
    },
    [onRemove, toastId]
  );
  return (
    <AppInboxToastBaseWrapper
      {...boxProps}
      data-testid={dataTestId}
      size="Default"
      variant={variant}
      w={wrapperWidth ? parseDimension(theme, wrapperWidth) : '100%'}
      onClick={handleClick}
    >
      <VStack
        gap="spacing4"
        alignItems="flex-start"
        w="100%"
        fontSize={theme.fontSizeSm}
        lineHeight="16px"
        cursor="default"
      >
        <HStack
          data-testid="toast-title-row"
          justifyContent="stretch"
          alignItems="center"
          w="100%"
          display="flex"
          gap="spacing4"
        >
          <AppInboxSeverityIcon severity={variant} />
          <EllipsisBoxWithTooltip
            data-testid="toast-title"
            outerBoxProps={{ flex: 1 }}
            alignItems="center"
            justifyContent="flex-start"
            wordBreak="break-all"
            fontWeight={theme.fontWeightMedium}
          >
            {title}
          </EllipsisBoxWithTooltip>
          <AppInboxToastBaseMenu
            timeStamp={timeStamp}
            containerVariant={containerVariant}
            handleRemove={handleRemove}
          />
        </HStack>
        {description && (
          <EllipsisBoxWithTooltip
            data-testid="toast-description"
            w="100%"
            color="colors.gray.090"
            lineClamp={lineClamp}
          >
            {description}
          </EllipsisBoxWithTooltip>
        )}
      </VStack>
    </AppInboxToastBaseWrapper>
  );
};

const AppInboxToastBaseMenu = ({
  timeStamp,
  containerVariant,
  handleRemove,
}: Pick<AppInboxToastBaseProps, 'timeStamp' | 'containerVariant'> & {
  handleRemove: React.MouseEventHandler<HTMLElement>;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  return (
    <HStack color="colors.gray['090']" gap="spacing2">
      <ToastDate timeStamp={timeStamp} />
      <Menu
        data-testid="toast-menu"
        menuPopoverZIndex="globalToastPopover"
        menuButtonIconProps={{
          // override the width, since the default width is measured by the height of the icon
          width: `${theme.spacing4}px`,
          height: `${theme.spacing16}px`,
          color: theme.colors.gray['090'],
        }}
      >
        <MenuItem
          data-testid="toast-menu-item-notification-settings"
          onClick={e => {
            navigate(SettingsPaths.UserNotifications());
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          Notification Settings
        </MenuItem>
      </Menu>
      <Icon
        data-testid="toast-close"
        onClick={handleRemove}
        icon={containerVariant === 'inbox' ? IconName.Trash : IconName.Close}
        size="fontSizeSmall"
        spaceBefore="auto"
      />
    </HStack>
  );
};

function getBorderTopColor(theme: DefaultTheme, variant?: AppNotificationSeverity) {
  let borderTopColor: string | undefined = undefined;
  switch (variant) {
    case 'High':
      borderTopColor = theme.colors.red.lighten;
      break;
    case 'Medium':
      borderTopColor = theme.colors.yellow.lighten;
      break;
    default:
      borderTopColor = undefined;
      break;
  }
  return borderTopColor;
}

const AppInboxToastBaseWrapper = styled(ToastSimpleWrapper)<{
  variant?: AppNotificationSeverity;
}>`
  ${({ theme, variant }: { theme: DefaultTheme; variant?: AppNotificationSeverity }) => {
    return `
     ${variant === AppNotificationSeverity.Low ? '' : `border-top: 2px solid ${getBorderTopColor(theme, variant)};`}
  `;
  }}
`;
